import React, { useState, useEffect } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  Button,
  Stack,
  TextField,
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  AccordionDetails,
  AccordionSummary,
  Accordion,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  useCreateCompanyControllerCreateCompanyMutation,
  DefaultSipSettingsDto,
  useVoiceListControllerVoiceListQuery,
  useLlmProviderListControllerAdminProviderListQuery,
  useTranscriberListControllerAdminTranscriberListQuery,
} from '@shared/services/apiService/apiService';
import { useStyles } from '../styles';
import UserCard from './UserCard';
import useTokenSwitcher from '../hooks/useTokenSwticher';
import ActionButtons from './ActionsButtons';
import { SipSettingsForm } from '@shared/components/forms/SipSettingsForm';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { IconChevronDown16 } from '@shared/assets/images/svgs';
import { UsageLimitsFields } from '../../CompanyLimitsPopup/components/UsageLimitsFields';
import {
  buildUsageLimitItemsFromForm,
  formatOptionLimitsConflictMessage,
} from '../../CompanyLimitsPopup/utils';
import { SelectVoicesInput } from '@widgets/CompanyVoices/SelectVoicesInput';
import { SelectLlmProvidersInput } from '@widgets/CompanyLlmProviders/SelectLlmProvidersInput';
import { SelectTranscribersInput } from '@widgets/CompanyTranscribers/SelectTranscribersInput';

export interface UserFormData {
  userLogin: string;
  temporaryPassword: string;
}

export interface CreateCompanyForm {
  companyName: string;
  description: string;
  maxParallelCalls: number;
  users: UserFormData[];
  defaultOutboundSettings: DefaultSipSettingsDto | null;
  defaultInboundSettings: DefaultSipSettingsDto | null;
  callHoursMonth: string;
  sttHoursMonth: string;
  llmTokensMonth: string;
  ttsCharactersMonth: string;
  voiceIds: string[];
  llmProviderIds: string[];
  transcriberIds: string[];
}

const CompanyForm: React.FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { showError, extractErrorMessage } = useErrorHandler();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [outboundSipValid, setOutboundSipValid] = useState(true);
  const [inboundSipValid, setInboundSipValid] = useState(true);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    setValue,
  } = useForm<CreateCompanyForm>({
    defaultValues: {
      companyName: '',
      description: '',
      maxParallelCalls: 10,
      users: [{ userLogin: '', temporaryPassword: '' }],
      defaultOutboundSettings: null,
      defaultInboundSettings: null,
      callHoursMonth: '',
      sttHoursMonth: '',
      llmTokensMonth: '',
      ttsCharactersMonth: '',
      voiceIds: [],
      llmProviderIds: [],
      transcriberIds: [],
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'users',
  });

  const [createCompany] = useCreateCompanyControllerCreateCompanyMutation();
  const { switchToken } = useTokenSwitcher();
  const theme = useTheme();

  const { data: catalogVoices } = useVoiceListControllerVoiceListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: catalogLlmProviders } = useLlmProviderListControllerAdminProviderListQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: catalogTranscribers } = useTranscriberListControllerAdminTranscriberListQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    setValue('voiceIds', []);
    setValue('llmProviderIds', []);
    setValue('transcriberIds', []);
  }, [setValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const onSubmit = async (data: CreateCompanyForm) => {
    setErrorMessage(null);

    const superAdminAccessToken = localStorage.getItem('superAdminAccessToken') || '';

    const isValid = await trigger();
    const limits = buildUsageLimitItemsFromForm({
      callHoursMonth: data.callHoursMonth,
      sttHoursMonth: data.sttHoursMonth,
      llmTokensMonth: data.llmTokensMonth,
      ttsCharactersMonth: data.ttsCharactersMonth,
    });

    if (limits === null) {
      showError({
        title: t('superAdminMainPage.companies.companyLimits.validationTitle'),
        message: t('superAdminMainPage.companies.companyLimits.invalidUsageLimitValue'),
      });
      return;
    }

    if (isValid) {
      await switchToken(superAdminAccessToken, async () => {
        try {
          await createCompany({
            createCompanyRequestDto: {
              name: data.companyName,
              description: data.description,
              defaultOutboundSettings: data.defaultOutboundSettings || undefined,
              defaultInboundSettings: data.defaultInboundSettings || undefined,
              limits: limits.length > 0 ? limits : undefined,
              companyConcurrentLimit: Number(data.maxParallelCalls),
              voiceIds: data.voiceIds,
              llmProviderIds: data.llmProviderIds,
              transcriberIds: data.transcriberIds,
              users: data.users.map((user) => ({
                email: user.userLogin,
                password: user.temporaryPassword,
                firstName: '',
                lastName: '',
                role: 'admin',
              })),
            },
          }).unwrap();

          toast.success(CustomSuccessToast, {
            closeButton: false,
            autoClose: 5000,
            icon: false,
            data: {
              title: t('common.saved'),
              description:
                t('superAdminMainPage.companies.successMessage', {
                  name: data.companyName,
                }) || `Company "${data.companyName}" created successfully`,
            },
          });

          closeDialog();
          reset();
        } catch (error) {
          const fetchError = error as FetchBaseQueryError;
          const extracted = extractErrorMessage(error);
          const voiceNameById = new Map<string, string>();
          for (const voice of catalogVoices ?? []) {
            voiceNameById.set(voice.id, voice.name);
          }
          const providerTitleById = new Map<string, string>();
          for (const provider of catalogLlmProviders ?? []) {
            providerTitleById.set(provider.id, provider.title);
          }
          const message = formatOptionLimitsConflictMessage(
            extracted.message,
            voiceNameById,
            providerTitleById,
          );

          if (fetchError.status === 409 && message.toLowerCase().includes('voice')) {
            showError({
              title: t('superAdminMainPage.companies.companyLimits.voiceConflictTitle'),
              message,
              suggestion: t('superAdminMainPage.companies.companyLimits.voiceConflictSuggestion'),
            });
          } else if (fetchError.status === 409 && message.toLowerCase().includes('provider')) {
            showError({
              title: t('superAdminMainPage.companies.companyLimits.llmProviderConflictTitle'),
              message,
              suggestion: t(
                'superAdminMainPage.companies.companyLimits.llmProviderConflictSuggestion',
              ),
            });
          } else {
            showError({
              title: t('superAdminMainPage.companies.createFailed'),
              message,
              suggestion: 'Please check your input and try again',
            });
          }

          setErrorMessage(message || t('common.errorMessage'));
        }
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Controller
              name="companyName"
              control={control}
              rules={{
                required: t('superAdminMainPage.companies.errors.companyNameError'),
                maxLength: {
                  value: 256,
                  message: t('superAdminMainPage.companies.errors.companyNameError'),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('superAdminMainPage.companies.company.name')}
                  variant="outlined"
                  fullWidth
                  error={!!errors.companyName}
                  helperText={errors.companyName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              rules={{
                maxLength: {
                  value: 256,
                  message: t('superAdminMainPage.companies.errors.descriptionError'),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('superAdminMainPage.companies.company.description')}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Accordion
                disableGutters
                sx={{
                  width: '100% !important',
                  boxShadow: 'unset',
                  '&:before': { display: 'none' },
                }}>
                <AccordionSummary
                  expandIcon={<IconChevronDown16 />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  sx={{
                    padding: '0px',
                    justifyContent: 'flex-start',
                    '& .MuiAccordionSummary-content': {
                      flexGrow: 'unset',
                    },
                    'svg path': {
                      stroke: theme.palette.primary.main,
                    },
                  }}>
                  <Typography
                    component="span"
                    sx={{
                      paddingRight: '6px',
                      color: theme.palette.primary.main,
                    }}>
                    {t('superAdminMainPage.companies.company.usageLimits')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: '0px',
                    display: 'block',
                  }}>
                  <Controller
                    name="maxParallelCalls"
                    control={control}
                    rules={{
                      required: t('superAdminMainPage.companies.errors.maxParallelCallsError'),
                      pattern: {
                        value: /^[0-9]+$/,
                        message: t('superAdminMainPage.companies.errors.maxParallelCallsError'),
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t('superAdminMainPage.companies.company.maxParallelCalls')}
                        variant="outlined"
                        fullWidth
                        type="number"
                        inputProps={{ min: 1 }}
                        error={!!errors.maxParallelCalls}
                        helperText={errors.maxParallelCalls?.message}
                      />
                    )}
                  />
                  <UsageLimitsFields control={control} />
                </AccordionDetails>
              </Accordion>
              <Accordion
                disableGutters
                sx={{
                  width: '100% !important',
                  boxShadow: 'unset',
                  '&:before': { display: 'none' },
                }}>
                <AccordionSummary
                  expandIcon={<IconChevronDown16 />}
                  aria-controls="panel-option-limits-content"
                  id="panel-option-limits-header"
                  sx={{
                    padding: '0px',
                    justifyContent: 'flex-start',
                    '& .MuiAccordionSummary-content': {
                      flexGrow: 'unset',
                    },
                    'svg path': {
                      stroke: theme.palette.primary.main,
                    },
                  }}>
                  <Typography
                    component="span"
                    sx={{
                      paddingRight: '6px',
                      color: theme.palette.primary.main,
                    }}>
                    {t('superAdminMainPage.companies.company.optionLimits')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: '0px',
                    display: 'block',
                  }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('superAdminMainPage.companies.company.voices')}
                  </Typography>
                  <Controller
                    name="voiceIds"
                    control={control}
                    render={({ field }) => (
                      <SelectVoicesInput
                        voices={catalogVoices}
                        selectedIds={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    {t('superAdminMainPage.companies.company.llmProviders')}
                  </Typography>
                  <Controller
                    name="llmProviderIds"
                    control={control}
                    render={({ field }) => (
                      <SelectLlmProvidersInput
                        providers={catalogLlmProviders}
                        selectedIds={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    {t('superAdminMainPage.companies.company.transcribers')}
                  </Typography>
                  <Controller
                    name="transcriberIds"
                    control={control}
                    render={({ field }) => (
                      <SelectTranscribersInput
                        transcribers={catalogTranscribers}
                        selectedIds={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.userCard}>
              <Stack spacing={2} width="100%">
                {fields.map((field, index) => (
                  <UserCard
                    key={field.id}
                    index={index}
                    control={control}
                    errors={errors}
                    remove={remove}
                  />
                ))}
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => append({ userLogin: '', temporaryPassword: '' })}
                  className={classes.addButton}
                  disabled={fields.length >= 3}>
                  {t('superAdminMainPage.companies.operation.addUser')}
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* SIP Settings Tabs */}
        <Box sx={{ mt: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label={t('superAdminMainPage.companies.sipSettingsTabs')}>
            <Tab label={t('superAdminMainPage.companies.tabs.sipOutbound')} />
            <Tab label={t('superAdminMainPage.companies.tabs.sipInbound')} />
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <Controller
                name="defaultOutboundSettings"
                control={control}
                render={({ field }) => (
                  <SipSettingsForm
                    settings={field.value}
                    onChange={field.onChange}
                    onValidationChange={setOutboundSipValid}
                    type="outbound"
                  />
                )}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ mt: 2 }}>
              <Controller
                name="defaultInboundSettings"
                control={control}
                render={({ field }) => (
                  <SipSettingsForm
                    settings={field.value}
                    onChange={field.onChange}
                    onValidationChange={setInboundSipValid}
                    type="inbound"
                  />
                )}
              />
            </Box>
          )}
        </Box>

        {errorMessage && (
          <Typography color="error" className={classes.errorMessage}>
            {errorMessage}
          </Typography>
        )}
        <ActionButtons closeDialog={closeDialog} disabled={!outboundSipValid || !inboundSipValid} />
      </Box>
    </form>
  );
};

export default CompanyForm;
